import { userSuggestionStatuses } from 'entities';

import { Column, ResponsiveOverlay, Row, Skeleton } from 'components/Blocks';
import { MultiLineText } from 'components/MultiLineText';
import { UserAvatar } from 'components/UserAvatar';
import { EntityLoader, EntityStatusTag } from 'components/Entities';
import { AttachedFiles } from 'components/Uploads';

import { useSuggestionModal } from './suggestionModal.hook';
import { SuggestionComments } from './SuggestionComments';

import { getTimePassed } from 'utils/date';
import { UpvoteSuggestionButton } from '../UpvoteSuggestionButton';
import { SuggestionLink } from './Link';

interface FocusedSuggestionProps {
  changeTab?: (tab: string) => void;
}

export function SuggestionModal({ changeTab }: FocusedSuggestionProps) {
  const { closeSuggestion, suggestionId } = useSuggestionModal();

  if (!suggestionId) return null;

  return (
    <ResponsiveOverlay
      default="sheet"
      close={closeSuggestion}
      isOpen
      largeClassName="max-w-md"
      className="overflow-auto"
    >
      <EntityLoader
        target="feedback.findSuggestion"
        params={{ suggestionId }}
        refreshMinutes={1}
        loading={<Skeleton quantity={3} heights={[72, 36, 26]} />}
        onLoad={({ status }) =>
          changeTab?.(status === userSuggestionStatuses.closed.code ? 'closed' : 'open')
        }
      >
        {({ data, overwrite }) => (
          <>
            <Row gap="2" className="mt-2">
              <UpvoteSuggestionButton
                suggestion={data}
                className="-ml-2"
                updateSuggestion={overwrite as () => void}
              />

              <EntityStatusTag
                target="userSuggestion"
                status={data.status}
                round={false}
                className="w-fit"
                size="medium"
              />

              <SuggestionLink suggestionId={suggestionId} />

              <span className="ml-auto text-xs">{getTimePassed(data.timestamp)}</span>
            </Row>

            <h5 className="text-lg">{data.title}</h5>

            <Column gap="1">
              <MultiLineText
                text={data.text}
                renderLine={(line) => (
                  <p className="max-w-[95ch] text-justify leading-relaxed">{line}</p>
                )}
              />
            </Column>

            <UserAvatar user={data.user} name size={32} />

            <AttachedFiles files={data.files} />

            <SuggestionComments suggestion={data} updateSuggestion={overwrite} />
          </>
        )}
      </EntityLoader>
    </ResponsiveOverlay>
  );
}
